import { Banner } from "@/components/flexkit/Banner";
import { formatShortDate } from "@/format/time";
import { useGetBillingInfo } from "@/queries/organization-queries";
import { Timestamp } from "@bufbuild/protobuf";
import { PaymentFailureState } from "gitpod-next-api/gitpod/v1/billing_pb";
import { useFlag } from "@/hooks/use-flag";
import { Flags } from "@/feature-flags";
import { type FC } from "react";

export const FailedPaymentBanner: FC = () => {
    const { value: featureFlagEnabled } = useFlag(Flags.EnableFailedPaymentReconciler, false);
    const { data: billingInfo } = useGetBillingInfo();

    if (!featureFlagEnabled) {
        return null;
    }

    const paymentFailure = billingInfo?.paymentFailure;
    
    if (!paymentFailure) {
        return null;
    }

    // Only show banner for IN_GRACE or SUSPENDED states
    if (paymentFailure.state !== PaymentFailureState.IN_GRACE && paymentFailure.state !== PaymentFailureState.SUSPENDED) {
        return null;
    }

    if (!paymentFailure.gracePeriodEndsAt) {
        return null;
    }

    const endDate = new Timestamp(paymentFailure.gracePeriodEndsAt).toDate();
    const now = new Date();
    const isGracePeriodExpired = endDate <= now;

    // If grace period has expired or state is SUSPENDED, show warning banner
    if (isGracePeriodExpired || paymentFailure.state === PaymentFailureState.SUSPENDED) {
        const formattedEndDate = formatShortDate(endDate, true);

        return (
            <Banner
                variant="warning"
                text={`Payment for most recent billing period has failed. Your subscription was suspended on ${formattedEndDate}, and your organization has been moved to the free tier. You may upgrade to Core again at any time.`}
                data-testid="suspended-payment-banner"
            />
        );
    }

    // Grace period is still active, show danger banner
    const formattedEndDate = formatShortDate(endDate, true);

    return (
        <Banner
            variant="danger"
            text={`Payment for most recent billing period has failed. Please resolve the payment issue in the Stripe Billing Portal. Your subscription is in a grace period ending on ${formattedEndDate}, after which your organization will be moved to the free tier.`}
            data-testid="failed-payment-banner"
        />
    );
};
